import { useRef, useEffect } from 'react';

/**
 * useClickOutside is a custom hook that detects clicks outside of a specified element.
 * @param action - The function to call when a click outside the element is detected.
 *   Typically to disactivate or dismiss something.
 * @returns A ref to be attached to the element you want to monitor for outside clicks.
 */
export function useClickOutside<T extends HTMLElement>(action: () => void) {
  const actionRef = useRef(action);
  actionRef.current = action;
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;

      // Make sure clicks on the Filestack Pick do not cause deselection.
      // Temp fix until we figure how to generalize this.
      const filestackPicker = document.getElementById('__filestack-picker');
      if (filestackPicker?.contains(target)) return;

      // "--preview" is a descendant by "__filestack-picker" yet contains returns false. Workaround:
      if (filestackPicker && target.nodeType === Node.ELEMENT_NODE && (target as Element).className === '--preview') return;

      if (elementRef.current?.contains(target)) return;
      actionRef.current?.();
    };
    addEventListener('click', handleClick);
    return () => removeEventListener('click', handleClick);
  }, []);

  return elementRef;
}
